import { createAsyncThunk } from '@reduxjs/toolkit';

import { TrackingEventEnum } from '../../../../main/constants/tracking-event.ts';
import { ThunkApiConfigType } from '../../../../main/store/main.store.ts';
import { getAwsAuthUtil } from '../../../../main/utils/aws/aws-auth.util.ts';
import { getLocaleUtil } from '../../../../main/utils/locale.util.ts';
import { getToasterUtil } from '../../../../main/utils/toaster.util.ts';
import { getTrackerUtil } from '../../../../main/utils/trackers/tracker.util.ts';
import { getUrlUtil } from '../../../../main/utils/url.util.ts';

import { AuthenticationMethodsEnum } from '../../../constants/authentication-methods.ts';
import { selectAccountEmail } from '../../../selectors/account-email.selector.ts';
import { MagicLinkOriginEnum, MagicLinkStatusEnum } from '../../../types/magic-link.types.ts';
import { getPlatformMagicLinkUtil } from '../../../utils/platform-magic-link.util.ts';

import { AUTH_SLICE_NAME } from '../auth.slice.types.ts';

import { fetchAndAssignCurrentUserThunk } from './fetch-and-assign-current-user.thunk.ts';
import { signOutThunk } from './sign-out.thunk.ts';

export const processMagicAuthTokenThunk = createAsyncThunk<void, void, ThunkApiConfigType>(
  `${AUTH_SLICE_NAME}/processMagicAuthTokenThunk`,
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();

    const { token } = getUrlUtil().getQueryParams(window.location.href);
    const magicToken = token?.[0];
    if (!magicToken) {
      return;
    }

    const initialEmail = selectAccountEmail(state);
    if (!initialEmail) {
      await thunkAPI.dispatch(signOutThunk()).unwrap();
    }

    const response = await getPlatformMagicLinkUtil().login({
      magicToken,
      clearCurrentAuthCookies: Boolean(initialEmail),
    });
    const result = getUrlUtil().removeQueryParams(window.location.href, ['token']);
    if (result.isRemoved) {
      getUrlUtil().replaceUrlState(result.url);
    }

    if (!response) {
      getTrackerUtil().track({
        event: TrackingEventEnum.MAGIC_LINK_AUTHENTICATION_COMPLETE,
        data: {
          origin: MagicLinkOriginEnum.STUDY_REEL,
          status: MagicLinkStatusEnum.FAIL,
        },
      });
      getToasterUtil().error(getLocaleUtil().select('auth:auth_token_failed'));
      return;
    }

    await getAwsAuthUtil().forceRefreshToken();

    const currentUser = await getAwsAuthUtil().getCurrentUser();
    if (!currentUser) {
      getTrackerUtil().track({
        event: TrackingEventEnum.MAGIC_LINK_AUTHENTICATION_COMPLETE,
        data: {
          origin: MagicLinkOriginEnum.STUDY_REEL,
          status: MagicLinkStatusEnum.FAIL,
        },
      });
      getToasterUtil().error(getLocaleUtil().select('auth:auth_token_failed'));
      return;
    }

    if (Boolean(initialEmail) && initialEmail !== currentUser.email) {
      await thunkAPI.dispatch(signOutThunk({ noAuthTokenReset: true })).unwrap();
    }

    await thunkAPI.dispatch(fetchAndAssignCurrentUserThunk()).unwrap();

    getTrackerUtil().track({
      event: TrackingEventEnum.PARENT_SIGN_IN,
      data: {
        method: AuthenticationMethodsEnum.MAGIC_LINK,
      },
    });
    getTrackerUtil().track({
      event: TrackingEventEnum.MAGIC_LINK_AUTHENTICATION_COMPLETE,
      data: {
        origin: MagicLinkOriginEnum.STUDY_REEL,
        status: MagicLinkStatusEnum.SUCCESS,
      },
    });
  }
);
